import { getApiClient, type RequestOpts } from './api-client';
import { ROTATION_SERVICE_URL as servicePath } from '../constants/APIconstants';
import { type EditableTeamGroup, type TeamGroup } from '../constants/rotations.constants';

export interface KnowledgeRef {
  id: string;
  type: string;
  url?: string;
}

export interface Rotation {
  id: string;
  organizationId: string;
  ownerIds: string[];
  code: string;
  name: string;
  knowledge?: KnowledgeRef[];
  url?: string;
  team?: Team;
  // Other fields populated by the server (schedule, welcomeLetter, ...) are
  // intentionally loose here — refine in consumers as needed.
  [key: string]: unknown;
}

export interface RotationTimeslot {
  id: string;
  start: string;
  end: string;
  userIds: string[];
  scheduleId: string;
  rotation: Rotation;
}

export interface GetTimeslotsParams extends RequestOpts {
  userIds?: string[];
  start: Date;
  end: Date;
}

export interface CreateRotationPayload {
  name: string;
  ownerIds: string[];
  code: string;
  scheduleId?: string;
  isAcgmeRequired?: boolean;
}

export interface RemoveKnowledgeArgs {
  rotationId: string;
  id: string;
}

export interface AddKnowledgeArgs {
  rotationId: string;
  payload: KnowledgeRef;
}

export interface UpdateRotationArgs {
  rotationId: string;
  payload: Record<string, unknown>;
}

export interface DeleteRotationPayload {
  destinationRotationId?: string;
  cloneContent?: boolean;
}

export interface DeleteRotationArgs {
  rotationId: string;
  payload: DeleteRotationPayload;
}

export interface WelcomeLetterPreviewPayload {
  subject: string;
  body: string;
}

export interface SendPreviewWelcomeLetterArgs {
  rotationId: string;
  payload: WelcomeLetterPreviewPayload;
}

export interface WelcomeLetterPayload {
  deliverDaysBefore: number | null;
  subject: string;
  body: string;
}

export interface UpdateWelcomeLetterArgs {
  rotationId: string;
  payload: WelcomeLetterPayload;
}

export interface UpdateRotationOverviewArgs {
  rotationId: string;
  payload: { overview: string };
}

export interface TeamMemberRef {
  id: string;
  type: string;
}

export type Team = Partial<Record<TeamGroup, TeamMemberRef[]>>;

export interface AddMembersToTeamArgs {
  rotationId: string;
  group: EditableTeamGroup;
  payload: TeamMemberRef[];
}

export interface RemoveMemberFromTeamArgs {
  rotationId: string;
  group: EditableTeamGroup;
  memberId: string;
}

export interface CreateRotationsTimeSlotsArgs {
  rotations: unknown[];
  skipEmail: boolean;
  createUserIfNotExits: boolean;
}

export interface CreateRotationsTimeSlotsResult {
  successful: Array<{ user: { email: string; created: boolean } }>;
  failed: string[];
}

/**
 * Rotations API. Methods throw on failure (axios default), return data
 * directly, and accept `{ signal }` for AbortSignal-based cancellation.
 *
 * Consumers should reach this through the hooks in `shared/hooks/rotations.hooks`;
 * direct consumption is only appropriate inside `queryFn` / `mutationFn` bodies.
 *
 * Most methods are thin pass-throughs to `getApiClient()` — the types are the
 * real contract. One option down the line is to inline these into the hooks and
 * keep this as a types module plus the few URL/payload helpers; left as-is for
 * now pending a consistent direction across all service files.
 */
class RotationsService {
  getTimeslots({ userIds, start, end, ...opts }: GetTimeslotsParams): Promise<RotationTimeslot[]> {
    const search = new URLSearchParams();
    if (userIds) for (const id of userIds) search.append('userIds', id);
    search.append('start', start.toJSON());
    search.append('end', end.toJSON());
    return getApiClient().get<RotationTimeslot[]>(`${servicePath}timeslot?${search}`, opts);
  }

  getRotationsList(opts?: RequestOpts): Promise<Rotation[]> {
    return getApiClient().get<Rotation[]>(`${servicePath}list`, opts);
  }

  getRotationFromUser(opts?: RequestOpts): Promise<Rotation> {
    return getApiClient().get<Rotation>(`${servicePath}user`, opts);
  }

  getAllRotations(opts?: RequestOpts): Promise<Rotation[]> {
    return getApiClient().get<Rotation[]>(`${servicePath}`, opts);
  }

  getRotationById(id: string, opts?: RequestOpts): Promise<Rotation> {
    return getApiClient().get<Rotation>(`${servicePath}${id}`, opts);
  }

  getTrainees(rotationId: string, opts?: RequestOpts): Promise<unknown[]> {
    return getApiClient().get<unknown[]>(`${servicePath}${rotationId}/trainees`, opts);
  }

  createSchedule(payload: { name: string }): Promise<{ id: string }> {
    return getApiClient().post<{ id: string }>(`${servicePath}schedule`, payload);
  }

  createRotation(payload: CreateRotationPayload): Promise<Rotation> {
    return getApiClient().post<Rotation>(servicePath, payload);
  }

  removeKnowledge({ rotationId, id }: RemoveKnowledgeArgs): Promise<unknown> {
    return getApiClient().delete(`${servicePath}${rotationId}/knowledge/${id}`);
  }

  addKnowledge({ rotationId, payload }: AddKnowledgeArgs): Promise<unknown> {
    return getApiClient().post(`${servicePath}${rotationId}/knowledge`, payload);
  }

  updateRotation({ rotationId, payload }: UpdateRotationArgs): Promise<Rotation> {
    return getApiClient().put<Rotation>(`${servicePath}${rotationId}`, payload);
  }

  deleteRotation({ rotationId, payload }: DeleteRotationArgs): Promise<unknown> {
    return getApiClient().delete(`${servicePath}${rotationId}`, { data: payload });
  }

  sendPreviewWelcomeLetter({
    rotationId,
    payload,
  }: SendPreviewWelcomeLetterArgs): Promise<unknown> {
    return getApiClient().post(`${servicePath}${rotationId}/welcome-letter/preview`, payload);
  }

  updateWelcomeLetter({
    rotationId,
    payload,
  }: UpdateWelcomeLetterArgs): Promise<WelcomeLetterPayload> {
    return getApiClient().put<WelcomeLetterPayload>(
      `${servicePath}${rotationId}/welcome-letter`,
      payload,
    );
  }

  updateRotationOverview({
    rotationId,
    payload,
  }: UpdateRotationOverviewArgs): Promise<{ overview: string }> {
    return getApiClient().put<{ overview: string }>(
      `${servicePath}${rotationId}/overview`,
      payload,
    );
  }

  addMembersToTeam({ rotationId, group, payload }: AddMembersToTeamArgs): Promise<unknown> {
    return getApiClient().post(`${servicePath}${rotationId}/team/${group}`, payload);
  }

  removeMemberFromTeam({
    rotationId,
    group,
    memberId,
  }: RemoveMemberFromTeamArgs): Promise<unknown> {
    return getApiClient().delete(`${servicePath}${rotationId}/team/${group}/${memberId}`);
  }

  createRotationsTimeSlots({
    rotations,
    skipEmail,
    createUserIfNotExits,
  }: CreateRotationsTimeSlotsArgs): Promise<CreateRotationsTimeSlotsResult> {
    return getApiClient().post<CreateRotationsTimeSlotsResult>(`${servicePath}timeslots`, {
      rotations,
      skipEmail,
      createUserIfNotExits,
    });
  }
}

export default new RotationsService();
